// Single source of truth for embed UI-chrome strings. Keys are flat dot-paths;
// SdkUiOverrides/UiStringKey are derived from this object so they cannot drift.
//
// Contract:
// - Shipped keys are a public API for SDK consumers (`uiOverrides`): additive
//   only — never rename, remove, or repurpose a key.
// - English defaults live only here. Call sites resolve via useUiStrings()
//   (frontend) or an optional UiStringResolver param (pure common code) —
//   never an inline fallback.
// - Dynamic parts use {token} placeholders with interpolateUiString; one key
//   per English plural form, no ICU.
// See packages/frontend/src/components/common/Filters/AGENTS.md for the full
// mandate on adding strings to embed-reachable surfaces.
export const DEFAULT_UI_STRINGS = {
    'agentAttribution.changedBy': 'Changed by an agent for {person} · {surface}',
    'agentAttribution.serviceAccount': 'a service account',
    'agentAttribution.unknownPerson': 'an unknown person',
    'agentAttribution.mcp': 'MCP',
    'agentAttribution.inApp': 'In-app agent',
    'agentAttribution.slack': 'Slack agent',
    'agentAttribution.cli': 'CLI',
    'agentAttribution.api': 'API',
    'agentAttribution.dataApp': 'Data app',
    'agentAttribution.aiSummary': 'AI summary',
    'aiAccess.runAsMe': 'Run as me',
    'aiAccess.alsoNeeded': 'Also needed: {requirements}',
    'aiAccess.requirements.capabilities.read_discover': 'Read and discover',
    'aiAccess.requirements.capabilities.query': 'Query data',
    'aiAccess.requirements.capabilities.raw_sql': 'Raw SQL',
    'aiAccess.requirements.capabilities.content_write': 'Create and edit content',
    'aiAccess.requirements.capabilities.delete': 'Delete content',
    'aiAccess.requirements.capabilities.publish': 'Publish and share',
    'aiAccess.requirements.capabilities.deploy_upload': 'Deploy and upload',
    'aiAccess.requirements.capabilities.dbt_writeback': 'Git repository changes (dbt)',
    'aiAccess.requirements.capabilities.export': 'Export results',
    'aiAccess.requirements.capabilities.administration': 'Administration',
    'aiAccess.requirements.capabilities.external_tools': 'External tools',
    'aiAccess.requirements.contentWrites': 'Agent content writes',
    'aiAccess.requirements.warehouseConfirmation': 'warehouse confirmation for this project',
    'aiAccess.requirements.agentAccess': 'access to agents',
    'aiAccess.requirements.allowedProjects': 'this project in the allowed projects',
    'aiAccess.requirements.agentAction': 'an action agents can perform',
    'aiAccess.requirements.supportedAction': 'a supported agent action',
    'aiAccess.requirements.agentPermissions': 'agent permissions',
    'aiAccess.seeWhy': 'See why',
    'aiAccess.settings': 'Review agent identity',
    'aiAccess.projectSettings': 'Open project agent settings',
    'aiAccess.loadError': 'Could not load agent identity',
    'aiAccess.signInError': 'Agent sign-in failed',
    'skillMenu.header': 'Skills',
    'skillMenu.noneAvailable': 'No skills available for this agent',
    'skillMenu.noMatch': 'No skill matches "{query}". Keep typing to send as text.',
    'skillMenu.onePerMessage': 'One skill per message. Remove the current one to pick another.',
    'skillChip.arguments': 'Arguments: {hint}',
    'page.closeSidebar': 'Close sidebar',
    'page.closeDetails': 'Close details',
    'filters.closeFacet': 'Close {filter} filter',
    'filters.sectionSummary': '{set} of {total} set',
    'filters.sectionChanged': '{changed} changed',
    'metrics.previous': 'Previous metric',
    'metrics.next': 'Next metric',
    'metrics.closeExploration': 'Close metric exploration',
    'metrics.dateStart': 'Date range start',
    'metrics.dateEnd': 'Date range end',
    'metrics.granularity': 'Time granularity',
    'metrics.canvasLabel': 'Metrics canvas',
    'metrics.expandCanvas': 'Expand canvas',
    'metrics.exitExpandedCanvas': 'Exit expanded canvas',
    'metrics.connectionExists': 'These metrics are already connected in this direction.',
    'metrics.connectMetrics': 'Connect metrics',
    'metrics.connect': 'Connect',
    'metrics.connectionSource': 'Source metric',
    'metrics.connectionTarget': 'Target metric',
    'metrics.closeSidebar': 'Close {sidebar}',
    'metrics.savedTrees': 'Saved trees',
    'metrics.searchMetrics': 'Search metrics',
    'metrics.noMatchingMetrics': 'No matching metrics',
    'metrics.removeSelected': 'Remove selected',
    'metrics.addMetrics': 'Add metrics',
    'metrics.canvasPeriod': 'Canvas period',
    'metrics.treeActions': 'Tree actions',
    'metrics.details': 'Details for {metric}',
    'metrics.closeDetails': 'Close metric details',
    'metrics.changeIcon': 'Change icon for {metric}',
    'metrics.removeIcon': 'Remove icon',
    'metrics.closeIconPicker': 'Close icon picker',
    'metrics.editCategories': 'Edit categories for {metric}',
    'metrics.addCategory': 'Add category',
    'metrics.closeCategories': 'Close categories',
    'metrics.editCategory': 'Edit category {category}',
    'metrics.closeCategoryEditor': 'Close category editor',
    'metrics.categoryName': 'Category name',
    'metrics.deleteCategory': 'Delete category {category}',
    'metrics.categoryColor': 'Category color {color}',
    'metrics.removeCategory': 'Remove category {category} from metric',
    'metrics.readDescription': 'Read full description for {metric}',
    'metrics.closeDescription': 'Close description',
    'metrics.comparison': 'Metric comparison',
    'metrics.comparePreviousYear': 'Compare to previous year',
    'metrics.compareAnotherMetric': 'Compare to another metric',
    'metrics.compareMetricTooltip': 'Compare "{metric}" to another metric',
    'metrics.comparisonMetric': 'Comparison metric',
    'metrics.ownerDetails': 'Owner details for {owner}',
    'tileMenu.filters': 'Chart filters',
    'tileMenu.parameters': 'Chart parameters',
    'tileMenu.execution': 'Query execution details',
    'tileMenu.timezone': 'Chart time zone',
    'tileMenu.askAi': 'Ask AI Agent',
    'tileMenu.actions': 'Tile actions',
    'comments.title': 'Comments',
    'comments.close': 'Close comments',
    'comments.reply': 'Reply',
    'comments.actions': 'Comment actions',
    'tileMenu.exploreFromHere': 'Explore from here',
    'tileMenu.downloadData': 'Download data',
    'tileMenu.exportImage': 'Export image',
    'tileMenu.viewUnderlyingData': 'View underlying data',
    'dateZoom.defaultZoom': 'Default zoom',
    'dateZoom.none': 'None',
    'dateZoom.viewModeTooltip': 'Charts will display dates using their original granularity settings.',
    'dateZoom.appliesToOneChart': 'Applies to {n} chart not in a zoom control',
    'dateZoom.appliesToManyCharts': 'Applies to {n} charts not in a zoom control',
    'dateZoom.noChartsUseDefault': 'No charts use the default (every chart is in a zoom control)',
    'dateZoom.granularities.Second': 'Second',
    'dateZoom.granularities.Minute': 'Minute',
    'dateZoom.granularities.Hour': 'Hour',
    'dateZoom.granularities.Day': 'Day',
    'dateZoom.granularities.Week': 'Week',
    'dateZoom.granularities.Month': 'Month',
    'dateZoom.granularities.Quarter': 'Quarter',
    'dateZoom.granularities.Year': 'Year',
    'dateZoom.dateZoomLabel': 'Date zoom:',
    'dateZoom.onLabel': 'On:',
    'filters.summary.filterSingular': 'filter',
    'filters.summary.filterPlural': 'filters',
    'filters.summary.parameterSingular': 'parameter',
    'filters.summary.parameterPlural': 'parameters',
    'parameters.is': 'is',
    'parameters.clear': 'Clear parameter',
    'parameters.source.chart': 'from chart',
    'parameters.source.dashboard': 'from dashboard',
    'parameters.source.default': 'default',
    'filters.summary.dateZoomLabel': 'Date Zoom:',
    'filters.summary.default': 'Default',
    'filters.summary.showFilters': 'Show filters',
    'filters.panel.title': 'Filters and parameters',
    'filters.panel.close': 'Close filters',
    'filters.operators.isNull': 'is null',
    'filters.operators.notNull': 'is not null',
    'filters.operators.equals': 'is',
    'filters.operators.notEquals': 'is not',
    'filters.operators.startsWith': 'starts with',
    'filters.operators.endsWith': 'ends with',
    'filters.operators.doesNotInclude': 'does not include',
    'filters.operators.include': 'includes',
    'filters.operators.lessThan': 'is less than',
    'filters.operators.lessThanOrEqual': 'is less than or equal',
    'filters.operators.greaterThan': 'is greater than',
    'filters.operators.greaterThanOrEqual': 'is greater than or equal',
    'filters.operators.inThePast': 'in the last',
    'filters.operators.notInThePast': 'not in the last',
    'filters.operators.inTheNext': 'in the next',
    'filters.operators.inTheCurrent': 'in the current',
    'filters.operators.notInTheCurrent': 'not in the current',
    'filters.operators.inBetween': 'is between',
    'filters.operators.notInBetween': 'is not between',
    'filters.operators.inPeriodToDate': 'in all',
    'filters.dateOperators.lessThan': 'is before',
    'filters.dateOperators.lessThanOrEqual': 'is on or before',
    'filters.dateOperators.greaterThan': 'is after',
    'filters.dateOperators.greaterThanOrEqual': 'is on or after',
    'filters.dateOperators.inBetween': 'is between',
    'filters.operators.inPeriodToDateDropdown': 'in all periods to date',
    'filters.operators.inPeriodToDateDescription': 'Trims every period in the range to the same point as today — e.g. with weeks selected, if today is Thursday, you get Mon–Thu of every week. Useful for like-for-like comparisons (WTD, MTD, QTD, YTD). For just the current period so far, use "in the current" instead.',
    'filters.nullValue': '(null)',
    'filters.betweenJoiner': 'and',
    'filters.selectValuePlaceholder': 'Select value',
    'filters.selectPeriodPlaceholder': 'Select period',
    'filters.periodToDate.years': 'year to date',
    'filters.periodToDate.quarters': 'quarter to date',
    'filters.periodToDate.months': 'month to date',
    'filters.periodToDate.weeks': 'week to date',
    'filters.periodToDate.fallback': 'period to date',
    'filters.periodToDateSelect.years': 'years to date',
    'filters.periodToDateSelect.quarters': 'quarters to date',
    'filters.periodToDateSelect.months': 'months to date',
    'filters.periodToDateSelect.weeks': 'weeks to date',
    'filters.currentPeriod.includeToday': 'Include today',
    'filters.currentPeriod.toDateValue': '{unit} to date',
    'filters.currentPeriod.toDateExcludingTodayValue': '{unit} to date, excluding today',
    'filters.unitsOfTime.milliseconds.singular': 'millisecond',
    'filters.unitsOfTime.milliseconds.plural': 'milliseconds',
    'filters.unitsOfTime.milliseconds.completedSingular': 'completed millisecond',
    'filters.unitsOfTime.milliseconds.completedPlural': 'completed milliseconds',
    'filters.unitsOfTime.seconds.singular': 'second',
    'filters.unitsOfTime.seconds.plural': 'seconds',
    'filters.unitsOfTime.seconds.completedSingular': 'completed second',
    'filters.unitsOfTime.seconds.completedPlural': 'completed seconds',
    'filters.unitsOfTime.minutes.singular': 'minute',
    'filters.unitsOfTime.minutes.plural': 'minutes',
    'filters.unitsOfTime.minutes.completedSingular': 'completed minute',
    'filters.unitsOfTime.minutes.completedPlural': 'completed minutes',
    'filters.unitsOfTime.hours.singular': 'hour',
    'filters.unitsOfTime.hours.plural': 'hours',
    'filters.unitsOfTime.hours.completedSingular': 'completed hour',
    'filters.unitsOfTime.hours.completedPlural': 'completed hours',
    'filters.unitsOfTime.days.singular': 'day',
    'filters.unitsOfTime.days.plural': 'days',
    'filters.unitsOfTime.days.completedSingular': 'completed day',
    'filters.unitsOfTime.days.completedPlural': 'completed days',
    'filters.unitsOfTime.weeks.singular': 'week',
    'filters.unitsOfTime.weeks.plural': 'weeks',
    'filters.unitsOfTime.weeks.completedSingular': 'completed week',
    'filters.unitsOfTime.weeks.completedPlural': 'completed weeks',
    'filters.unitsOfTime.months.singular': 'month',
    'filters.unitsOfTime.months.plural': 'months',
    'filters.unitsOfTime.months.completedSingular': 'completed month',
    'filters.unitsOfTime.months.completedPlural': 'completed months',
    'filters.unitsOfTime.quarters.singular': 'quarter',
    'filters.unitsOfTime.quarters.plural': 'quarters',
    'filters.unitsOfTime.quarters.completedSingular': 'completed quarter',
    'filters.unitsOfTime.quarters.completedPlural': 'completed quarters',
    'filters.unitsOfTime.years.singular': 'year',
    'filters.unitsOfTime.years.plural': 'years',
    'filters.unitsOfTime.years.completedSingular': 'completed year',
    'filters.unitsOfTime.years.completedPlural': 'completed years',
    'filters.addFilter': 'Add filter',
    'filters.apply': 'Apply',
    'filters.isAnyValue': 'is any value',
    'filters.requiredFilterTooltip': 'This is a required filter defined in the model configuration and cannot be removed.',
    'filters.placeholders.anyValue': 'any value',
    'filters.placeholders.enterValue': 'Enter value',
    'filters.placeholders.enterValues': 'Enter value(s)',
    'filters.placeholders.startTyping': 'Start typing to filter results',
    'filters.placeholders.selectDate': 'Select a date',
    'filters.placeholders.selectDates': 'Select date(s)',
    'filters.placeholders.selectValue': 'Select a value',
    'filters.autocomplete.loading': 'Loading...',
    'filters.autocomplete.noResults': 'No results found',
    'filters.autocomplete.addValue': 'Add "{value}"',
    'filters.autocomplete.maxResultsContinue': 'Showing first {n} results. Continue typing...',
    'filters.autocomplete.maxResultsStart': 'Showing first {n} results. Start typing...',
    'filters.autocomplete.editValuesTooltip': 'Edit filter values',
    'filters.autocomplete.filterNotAvailable': 'Filter not available',
    'filters.values.true': 'True',
    'filters.values.false': 'False',
    'filters.config.filterSettingsTab': 'Filter Settings',
    'filters.config.filterSettingsTabTooltip': 'Select the value you want to filter your dimension by',
    'filters.config.tilesTab': 'Tiles',
    'filters.config.tilesTabTooltip': 'Select tiles to apply filter to and which field to filter by',
    'filters.config.tabsAndTilesTab': 'Tabs & tiles',
    'filters.config.tabsAndTilesTabTooltip': 'Select which tabs and tiles this filter applies to',
    'filters.config.selectField': 'Select a field to filter',
    'filters.config.selectFilterPlaceholder': 'Select a filter',
    'filters.config.searchFieldPlaceholder': 'Search field...',
    'filters.actions.cancel': 'Cancel',
    'filters.actions.clearAll': 'Clear all',
    'filters.config.applyRequiredTooltip': 'Filter field and value required',
    'filters.config.applyLockedRequiredTooltip': 'A locked, required filter must have a value',
    'filters.config.resetToOriginal': 'Reset to original value',
    'filters.config.resetToOriginalAria': 'Reset filter to original value',
    'filters.config.selectColumn': 'Select a column to filter',
    'filters.config.searchColumnPlaceholder': 'Search column...',
    'filters.config.noMatchingFields': 'No matching fields',
    'filters.config.moreFields.singular': '{count} more field. Type to search',
    'filters.config.moreFields.plural': '{count} more fields. Type to search',
    'filters.config.fieldsInThisTab': 'Fields in this tab',
    'filters.config.otherAvailableFields': 'Other available fields',
    'filters.config.valueLabel': 'Value',
    'filters.config.clearToAnyValue': 'Clear to any value',
    'filters.config.alreadyAnyValue': 'Already showing any value',
    'filters.config.noValueToClear': 'No value to clear',
    'filters.config.expandTab': 'Expand tab',
    'filters.config.collapseTab': 'Collapse tab',
    'filters.config.noTilesInTab': 'No tiles in this tab',
    'filters.config.noFieldsMatchingType': 'No fields matching filter type',
    'filters.config.fieldNotAvailableInChart': "The selected field '{field}' is not available in this chart",
    'filters.coverage.noMatchingCharts': 'No charts have a matching field for this filter.',
    'filters.coverage.reviewTileTargets': 'Review tile targets',
    'filters.coverage.wontAffectCurrentTab': "This filter won't affect charts on the current tab.",
    'filters.coverage.appliesAutomaticallyTo': 'It applies automatically to:',
    'filters.coverage.reviewAndChangeTarget': 'Review tile targets and change the filter target',
    'filters.notAppliedToAnyTiles': 'This filter is not applied to any tiles',
    'filters.notAppliedToAnyTabs': 'This filter is not applied to any tabs',
    'filters.filterIsLocked': 'Filter is locked',
    'filters.filterIsLockedOnTab': 'Filter is locked on this tab',
    'filters.tableLabel': 'Table: ',
    'filters.tablesLabel': 'Tables: ',
    'filters.invalidFilter': 'Invalid filter',
    'filters.lockedFilterTooltip': 'This filter applies to your charts. You cannot change it here.',
    'filters.required.setValueTooltip': 'Required: set a value to run this dashboard',
    'filters.required.setValueGroupTooltip': 'Required: set a value on this or an alternative filter to run this dashboard',
    'filters.required.modalTitle': 'Set filters to load this dashboard',
    'filters.required.modalDescription': 'Data loads automatically once the filters below are set.',
    'filters.required.change': 'Change',
    'filters.required.setInToolbar': 'Set filters in the toolbar instead',
    'filters.crossFilter.menuLabel': 'Filter dashboard on {field} to',
    'filters.crossFilter.showOnly': 'Show only',
    'filters.crossFilter.exclude': 'Exclude',
    'filters.inputs.startDate': 'Start date',
    'filters.inputs.endDate': 'End date',
    'filters.inputs.minValue': 'Min value',
    'filters.inputs.maxValue': 'Max value',
    'filters.inputs.bothValuesRequired': 'Both values are required',
    'filters.inputs.minLessThanMax': 'Minimum should be less than the maximum',
    'filters.inputs.typeToAddValue': 'Please type to add the filter value',
    'filters.inputs.selectQuarter': 'Select Quarter',
    'filters.inputs.pasteDetected': 'Multiple comma-separated values detected:',
    'filters.inputs.pasteQuestion': 'Would you like to add them as single or multiple values?',
    'filters.inputs.singleValue': 'Single value',
    'filters.inputs.multipleValues': 'Multiple values',
    'filters.autocomplete.manageValuesTooltip': 'Manage filter values',
    'filters.autocomplete.refreshTooltip': 'Click to refresh filter values',
    'filters.autocomplete.resultsLoadedAt': 'Results loaded at {time}',
    'filters.autocomplete.error.configuration': "Suggestions unavailable: this field's autocomplete isn't set up correctly.",
    'filters.autocomplete.error.forbidden': "You don't have access to suggestions for this field.",
    'filters.autocomplete.error.warehouse': 'Suggestions unavailable: the warehouse query failed.',
    'filters.autocomplete.error.unknown': "Suggestions couldn't be loaded.",
    'filters.autocomplete.error.filterStillApplies': 'Your filter still applies. Type a value and press Enter.',
    'filters.autocomplete.error.tryAgain': 'Try again',
    'filters.autocomplete.error.retryAriaLabel': 'Retry loading suggestions',
    'filters.manageValues.title': 'Manage values',
    'filters.manageValues.filterValuesTitle': 'Manage filter values',
    'filters.manageValues.clearAllValues': 'Clear all values',
    'filters.manageValues.importCsv': 'Import CSV',
    'filters.manageValues.importCsvAria': 'Import CSV file',
    'filters.manageValues.selectAllShown': 'Select all shown',
    'filters.manageValues.selections': 'Selections',
    'filters.manageValues.searchPlaceholder': 'Search values…',
    'filters.manageValues.valuesCount': '{n} values',
    'filters.manageValues.selectedCount': '({n} selected)',
    'filters.manageValues.noValuesYet': 'No values yet',
    'filters.manageValues.noMatches': 'No matches',
    'filters.manageValues.tryDifferentSearch': 'Try a different search.',
    'filters.manageValues.importCsvHint': 'Import a CSV to populate this filter, then you can review and remove items here.',
    'filters.resetAll': 'Reset all filters',
    'filters.unsavedFiltersTooltip': 'Filters you add are not saved',
    'filters.summary.hideFilters': 'Hide filters',
    'filters.summary.hide': 'Hide',
    'filters.rail.newFilter': 'New filter',
    'filters.rail.filterName': 'Filter name',
    'filters.rail.removeFilter': 'Remove filter',
    'filters.rail.onlyYouSeeFilter': 'Only you see this filter',
    'filters.rail.collapse': 'Collapse',
    'filters.rail.tileCount.singular': '{count} tile',
    'filters.rail.tileCount.plural': '{count} tiles',
    'filters.rail.countOf': '{applied} of {total}',
    'filters.rail.tabFraction': '{applied}/{total}',
    'filters.rail.searchFields': 'Search fields',
    'filters.rail.noOtherField': 'No tile has another field this filter could use',
    'filters.rail.sqlColumn': 'SQL column',
    'filters.rail.thisFilter': 'this filter',
    'filters.rail.tabCountTooltip.singular': '{applied} of {total} tile on this tab is filtered by {subject}',
    'filters.rail.tabCountTooltip.plural': '{applied} of {total} tiles on this tab are filtered by {subject}',
    'filters.rail.sectionCount.singular': '{count} filter',
    'filters.rail.sectionCount.plural': '{count} filters',
    'filters.rail.untitledSection': 'Untitled section',
    'filters.rail.collapseSection': 'Collapse section {section}',
    'filters.rail.expandSection': 'Expand section {section}',
    'filters.rail.add': 'Add',
    'filters.rail.discard': 'Discard',
    'filters.rail.applyChangesFirst': 'Apply changes to {filter} first?',
    'filters.rail.appliesTo': 'Applies to {tiles}',
    'filters.rail.chooseTiles': 'Choose tiles',
    'filters.rail.addField': 'Add a field',
    'filters.rail.mappingFilters': 'filters',
    'filters.rail.backToFilter': 'Back to filter',
    'filters.rail.mappingUsing': 'using',
    'filters.tileCard.locked': 'Tiles are locked while a filter is edited',
    'filters.tileCard.unreachable': 'This tile has no field this filter can use',
    'filters.tileCard.filtered': 'This tile is filtered',
    'filters.tileCard.notFiltered': 'This tile is not filtered',
    'filters.tileCard.missingField': 'Filtered by a field this tile no longer has',
    'filters.tileCard.filterThisTile': 'Filter this tile',
    'filters.tileCard.selectField': 'Select a field',
    'filters.tileCard.selectColumn': 'Select a column',
    'filters.tileCard.stopFiltering': 'Stop filtering this tile',
    'filters.tileCard.noFieldsMatch': 'No fields match',
    'filters.tileCard.noColumnsMatch': 'No columns match',
    'filters.tileCard.thisTile': 'this tile',
    'filters.tileCard.selectName': '{filter} on {tile}',
    'filters.tileCard.filteredBy': 'Filtered by {field}',
    'filters.tileCard.filteredVia': 'Filtered via {field}',
    'filters.tileCard.filteredByHeading': 'Filtered by',
    'filters.tileCard.filteredViaHeading': 'Filtered via',
    'filters.tileCard.notFilteredLabel': 'Not filtered',
    'filters.tileCard.fieldNotOnTile': "The selected field '{field}' is not available on this tile",
    'filters.fieldBar.region': 'Tiles filtered by {field}',
    'filters.fieldBar.tilesOf.singular': 'tile',
    'filters.fieldBar.tilesOf.plural': 'tiles',
    'filters.fieldBar.noTile': 'No tile has this field',
    'filters.fieldBar.undo': 'Undo',
    'filters.fieldBar.clear': 'Clear',
    'filters.fieldBar.replacesTooltip': 'Replaces {fields} on {tiles}',
    'filters.fieldBar.twoFields': '{first} and {second}',
    'filters.fieldBar.otherFields': 'other fields',
    'filters.fieldBar.undoClearName': 'Undo clearing {field} from {tiles}',
    'filters.fieldBar.undoFilterName': 'Undo filtering {tiles} by {field}',
    'filters.fieldBar.filterMore': 'Filter {count} more',
    'filters.fieldBar.filterOnOtherTabs': 'Filter {count} on other tabs',
    'filters.fieldBar.filterMoreNone': 'Filter more',
    'filters.fieldBar.clearCount': 'Clear {count}',
    'filters.fieldBar.clearOnOtherTabs': 'Clear {count} on other tabs',
    'filters.fieldBar.onlyFieldClear': "This is the filter's only field",
    'dashboard.exportAllTiles': 'Export all tiles',
    'dashboard.printPage': 'Print this page',
    'dashboard.exportPdf': 'Export as PDF',
    'aiAgent.unavailable': "AI isn't available right now.",
    'aiAgent.modelSelector.retired': 'Retired',
    'aiAgent.modelSelector.agentDefault': 'Agent default',
    'aiAgent.composer.loadingResult': 'Loading results...',
    'aiAgent.composer.resultError': 'This result could not be loaded',
    'aiAgent.composer.emptyResult': 'No rows returned',
    'aiAgent.composer.view.chart': 'Chart',
    'aiAgent.composer.view.lineage': 'Lineage',
    'aiAgent.composer.lineage.focusNode': 'Focus {node}',
    'aiAgent.composer.lineage.resize': 'Resize lineage graph and steps',
    'aiAgent.composer.nodeStatus.pending': 'Queued',
    'aiAgent.composer.nodeStatus.awaitingApproval': 'Awaiting approval',
    'aiAgent.composer.nodeStatus.running': 'Running',
    'aiAgent.composer.nodeStatus.success': 'Completed',
    'aiAgent.composer.nodeStatus.error': 'Failed',
    'aiAgent.composer.node.viewResults': 'View results',
    'aiAgent.composer.node.viewResultsOf': 'View results of {node}',
    'aiAgent.composer.node.viewResultsCount.singular': 'View results ({count} row)',
    'aiAgent.composer.node.viewResultsCount.plural': 'View results ({count} rows)',
    'aiAgent.composer.node.collapse': 'Collapse step',
    'aiAgent.composer.node.expand': 'Expand step',
    'aiAgent.composer.node.addToChat': 'Add to chat',
    'aiAgent.composer.node.exploreFromHere': 'Explore from here',
    'aiAgent.composer.node.references': 'References',
    'aiAgent.composer.node.finalResult': 'Final result',
    'aiAgent.composer.node.sqlQuery': 'SQL query',
    'aiAgent.composer.semanticNode.metricQuery': 'Metric query',
    'aiAgent.composer.node.running': 'Running…',
    'aiAgent.composer.node.queued': 'Queued, waiting on the steps it reads',
    'aiAgent.composer.semanticNode.compiling': 'Compiling query…',
    'aiAgent.composer.semanticNode.compileError': 'Could not compile this query.',
    'aiAgent.composer.semanticNode.view': 'View metric query',
    'aiAgent.composer.semanticNode.hide': 'Hide metric query',
    'aiAgent.composer.semanticNode.viewSql': 'View SQL',
    'aiAgent.composer.semanticNode.hideSql': 'Hide SQL',
    'aiAgent.composer.semanticNode.explore': 'Explore',
    'aiAgent.composer.semanticNode.fields': 'Fields',
    'aiAgent.composer.semanticNode.filters': 'Filters',
    'aiAgent.composer.semanticNode.parameters': 'Parameters',
    'aiAgent.composer.semanticNode.sorts': 'Sorts',
    'aiAgent.composer.semanticNode.ascending': 'ascending',
    'aiAgent.composer.semanticNode.descending': 'descending',
    'aiAgent.composer.semanticNode.rows.singular': '{count} row',
    'aiAgent.composer.semanticNode.rows.plural': '{count} rows',
    'aiAgent.composer.node.rowsReachedLimit': '{rows} · reached limit',
    'aiAgent.thread.boundDashboard': 'Linked dashboard',
    'aiAgent.thread.boundDashboardUnavailable': 'Dashboard no longer available',
    'aiAgent.thread.boundChart': 'Linked chart',
    'aiAgent.thread.boundChartUnavailable': 'Chart no longer available',
    'explorer.back': 'Back',
    'explorer.backToDashboard': 'Back to Dashboard',
    'explorer.backToChart': 'Back to Chart',
    'explorer.backToAi': 'Back to AI',
    'explorer.backToMetricsCatalog': 'Back to Metrics Catalog',
};
export const interpolateUiString = (template, vars) => template.replace(/\{(\w+)\}/g, (match, name) => name in vars ? String(vars[name]) : match);
//# sourceMappingURL=uiStrings.js.map